/* Pfanner — guest Merkliste (wishlist), theme-side half of feature H3.
   Storage: localStorage 'pfanner:wishlist' = JSON array of product handles.
   The companion app later reconciles this with customer metafields on login;
   the storage shape here is the sync contract (keep it a plain handle array).

   Elements:
   - <wishlist-toggle data-handle data-title>  heart button (cards + PDP)
   - <wishlist-items>                          Merkliste page renderer
   - header badge: .header__icon--wishlist gets a count bubble            */

(() => {
  'use strict';
  const KEY = 'pfanner:wishlist';

  const read = () => {
    try { const v = JSON.parse(localStorage.getItem(KEY)); return Array.isArray(v) ? v : []; }
    catch { return []; }
  };
  const write = (handles) => {
    localStorage.setItem(KEY, JSON.stringify(handles));
    document.dispatchEvent(new CustomEvent('pfanner:wishlist:change', { detail: { handles } }));
  };

  const Wishlist = {
    list: read,
    has: (h) => read().includes(h),
    toggle(h) {
      const items = read();
      const i = items.indexOf(h);
      if (i >= 0) items.splice(i, 1); else items.push(h);
      write(items);
      return i < 0;
    },
    remove(h) { write(read().filter((x) => x !== h)); },
  };
  window.PfannerWishlist = Wishlist;

  const HEART = (filled) => `
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
         fill="${filled ? 'currentColor' : 'none'}" stroke="currentColor" stroke-width="2"
         stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
    </svg>`;

  customElements.define('wishlist-toggle', class extends HTMLElement {
    connectedCallback() {
      this.handle = this.dataset.handle;
      this.btn = document.createElement('button');
      this.btn.type = 'button';
      this.btn.className = 'wishlist-toggle__btn';
      this.btn.addEventListener('click', (e) => {
        e.preventDefault(); e.stopPropagation();
        Wishlist.toggle(this.handle);
      });
      this.append(this.btn);
      this.render();
      document.addEventListener('pfanner:wishlist:change', () => this.render());
    }
    render() {
      const on = Wishlist.has(this.handle);
      this.btn.innerHTML = HEART(on);
      this.btn.classList.toggle('is-active', on);
      const title = this.dataset.title || '';
      // Pfanner: aria-label used to be built from a hardcoded German template, which stayed
      // German on /en no matter the interface language (this runs client-side, bypassing the
      // Liquid translation layer). data-aria-add / data-aria-remove are meant to arrive
      // already translated + interpolated from Liquid (title is static per instance, so no
      // runtime marker needed) — German literals here are last-resort fallbacks only.
      const ariaAdd = this.dataset.ariaAdd || `${title} zur Merkliste hinzufügen`;
      const ariaRemove = this.dataset.ariaRemove || `${title} von der Merkliste entfernen`;
      this.btn.setAttribute('aria-label', on ? ariaRemove : ariaAdd);
      this.btn.setAttribute('aria-pressed', String(on));
    }
  });

  customElements.define('wishlist-items', class extends HTMLElement {
    connectedCallback() {
      this.emptyState = this.firstElementChild;
      this.grid = document.createElement('ul');
      this.grid.className = 'wishlist__grid';
      this.grid.setAttribute('role', 'list');
      this.append(this.grid);
      this.render();
      document.addEventListener('pfanner:wishlist:change', () => this.render());
      if (this.dataset.loggedIn === 'true') this.sync();
    }
    /* H3 account sync — merges the guest list with the customer metafield via
       the companion app proxy. Silent no-op until the app is installed. */
    async sync() {
      try {
        const res = await fetch('/apps/companion/wishlist', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ handles: Wishlist.list() }),
        });
        if (!res.ok) return;
        const data = await res.json();
        if (Array.isArray(data.handles)) write(data.handles);
      } catch { /* app not yet active */ }
    }
    async render() {
      const handles = Wishlist.list();
      const empty = handles.length === 0;
      if (this.emptyState) this.emptyState.hidden = !empty;
      this.grid.hidden = empty;
      if (empty) { this.grid.innerHTML = ''; return; }
      // Pfanner: this MUST go through Shopify.routes.root ('/' on de, '/en/' on en).
      // With a hardcoded `/products/…` the fetch always hit the PRIMARY locale, so on
      // /en/pages/merkliste every saved product came back with its German title and a
      // German `url` — an English visitor saw "100% Kokoswasser" instead of "100% Coconut
      // Water", and clicking the card dropped them out of /en back into the German
      // storefront. Found 2026-08-05 by rendering the populated wishlist on /en; no query
      // or grep would have shown it, because the German is fetched at runtime and the
      // Liquid translation layer never sees this list at all.
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const items = await Promise.all(handles.map(async (h) => {
        try { return await (await fetch(`${root}products/${h}.js`)).json(); }
        catch { return null; }
      }));
      // Pfanner: "Entfernen" was a hardcoded German literal baked into every card, invisible
      // to the Liquid translation layer since this whole list is built client-side from
      // /products/{handle}.js. data-remove-label on the <wishlist-items> host (rendered by
      // sections/wishlist.liquid) is meant to carry the translated string; German stays only
      // as the last-resort fallback.
      const removeLabel = this.dataset.removeLabel || 'Entfernen';
      this.grid.innerHTML = items.filter(Boolean).map((p) => `
        <li class="wishlist__item card" data-handle="${p.handle}">
          <a href="${p.url}" class="wishlist__item-media">
            ${p.featured_image ? `<img src="${p.featured_image}&width=360" alt="" loading="lazy" width="180" height="180">` : ''}
          </a>
          <div class="wishlist__item-info">
            <a href="${p.url}" class="wishlist__item-title">${p.title}</a>
            <button type="button" class="wishlist__item-remove link" data-remove="${p.handle}">${removeLabel}</button>
          </div>
        </li>`).join('');
      this.grid.querySelectorAll('[data-remove]').forEach((b) =>
        b.addEventListener('click', () => Wishlist.remove(b.dataset.remove)));
    }
  });

  // header heart badge
  const updateBadge = () => {
    const icon = document.querySelector('.header__icon--wishlist');
    if (!icon) return;
    let b = icon.querySelector('.wishlist-count-bubble');
    const n = read().length;
    if (!n) { b?.remove(); return; }
    if (!b) {
      b = document.createElement('div');
      b.className = 'cart-count-bubble wishlist-count-bubble';
      icon.append(b);
    }
    // Pfanner: the visually-hidden count text was a hardcoded German template built at
    // runtime (bypasses the Liquid translation layer). data-wishlist-count-label on the
    // .header__icon--wishlist anchor (sections/header.liquid) is meant to carry the
    // translated template with a __COUNT__ marker; German is the last-resort fallback.
    const tmpl = icon.dataset.wishlistCountLabel || '__COUNT__ Produkte auf der Merkliste';
    b.innerHTML = `<span aria-hidden="true">${n}</span><span class="visually-hidden">${tmpl.replace('__COUNT__', n)}</span>`;
  };
  document.addEventListener('pfanner:wishlist:change', updateBadge);
  if (document.readyState !== 'loading') updateBadge();
  else document.addEventListener('DOMContentLoaded', updateBadge);
})();
